import { useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { useTranslation } from 'react-i18next';
import { Box, Collapse, Stack, useTheme } from '@mui/material';
import {
  hash,
  emptyId,
  editorUtils,
  IFlowStepConfig,
  EditWorkflowType,
  generateTestIds,
  Skeleton,
  DefaultSkeletonLoader,
  useEditorStore,
  useFlowBuilder2Store,
  LayoutComponentNames,
  useAssistantRequest,
  FlowBuilder2TaskFieldNamesEnum,
  useFlowBuilder2MoveTaskMutation,
  FlowResourceType,
  isEmptyId,
  useEvents,
  Events,
} from '@diana/core';
import { DropBoxBody } from './FlowExplorer.styles';
import { NodeHeader } from './NodeHeader';
import { TaskNameField } from '../TaskNameField/TaskNameField';
import { isEqual, uniqBy } from 'lodash';
import { SectionDropPlaceholder, SectionNode } from './SectionNode';
import { useDeleteTask } from '../../Support/useDeleteTask';
import { FLOW_BUILDER2_PAGE_NS } from '../../i18n';
import { useAddSection } from '../../Support/useAddSection';
import { useFlowBuilder2PageStore } from '../../Store/useFlowBuilder2PageStore';
import { ExpandButton } from './ExpandButton';
import { useDeleteStep } from '../../Support/useDeleteStep';
import { useExpandItem } from './useExpandItem';
import { scrollToTask } from '../../Support/useBuilderScroll';

interface TaskNodeProps {
  taskId: string;
  taskIndex: number;

  stepId: string;
  stepIndex: number;

  level: number;

  canDrag?: boolean;
  isSingleTask: boolean;

  disableTopGutter?: boolean;
  disableBottomGutter?: boolean;
}

interface IDragItem {
  taskId: string;
  stepId: string;
}

interface IDragCollected {
  isDragging: boolean;
}

interface IDropCollected {
  isOver: boolean;
}

const DraggableTask = Symbol('task');

export const TaskNodeTestIds = generateTestIds('TaskNode', ['Root', 'ToggleCollapseButton']);

export const TaskNode = (props: TaskNodeProps) => {
  const { taskId, stepId, isSingleTask, level, canDrag } = props;

  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);

  const activeTaskId = useFlowBuilder2PageStore((s) => s.activeTaskId);
  const activeComponent = useFlowBuilder2PageStore((s) => s.activeComponent);
  const setActiveTaskId = useFlowBuilder2PageStore((s) => s.setActiveTaskId);

  const getFlow = useEditorStore((s) => s.getFlow);
  const updateFlow = useEditorStore((s) => s.updateFlow);

  const flowId = useEditorStore((s) => s.flow?.id ?? '');
  const isSingleStepInFlow = useEditorStore(
    (s) => uniqBy(s.flow?.steps, (step) => step.stepId).length === 1,
  );
  const layoutId = useEditorStore((s) => s.getTaskInFlowById(taskId)?.layoutId ?? '');
  const displayName = useEditorStore((s) => s.getTaskInFlowById(taskId)?.taskDisplayName ?? '');
  const isSelected = useEditorStore((s) => {
    if (activeTaskId) return activeTaskId === taskId;
    if (activeComponent) {
      const step = s.flow?.steps.find((step) => step.taskId === taskId);
      return step?.layoutId === activeComponent.layoutId;
    }
    return false;
  });
  const registerRequest = useFlowBuilder2Store((s) => s.registerRequest);
  const validationResult = useFlowBuilder2Store((s) => {
    return s.getFieldState(taskId, 'task', FlowBuilder2TaskFieldNamesEnum.DisplayName)
      .validationResult;
  });

  const sections = useEditorStore((s) => {
    const components = s.getLayoutById(layoutId)?.components ?? [];
    return components
      .map((component, index) => ({
        id: component.id,
        parentId: component.parentId,
        index,
        component: component.component,
      }))
      .filter(
        (component) =>
          isEmptyId(component.parentId) && component.component === LayoutComponentNames.SECTION,
      );
  }, isEqual);

  const isAssetType = useEditorStore(
    (store) => store.flow?.properties.flowResourceType === FlowResourceType.Entity,
  );

  const storeParams = { type: EditWorkflowType.Layout, flowId, layoutId, componentId: undefined };

  const { hasRequest } = useAssistantRequest(storeParams);

  const bodyRef = useRef<HTMLDivElement>(null);

  const [dropPlacement, setDropPlacement] = useState<'before' | 'after' | null>(null);

  const { palette, spacing } = useTheme();
  const { deleteTask, isDeletingTask } = useDeleteTask();
  const { deleteStep, isDeletingStep } = useDeleteStep();
  const { addSection } = useAddSection();
  const { handleExpand, isExpanded } = useExpandItem({ itemId: taskId, level });

  const { mutateAsync: moveTask } = useFlowBuilder2MoveTaskMutation({
    onSuccess: (flow) => {
      updateFlow((draft) => ({ ...draft, steps: flow.steps }));
    },
  });

  const onClick = () => {
    handleExpand(true);
    scrollToTask(taskId);
    setActiveTaskId(taskId);
  };

  const [{ isDragging }, drag, preview] = useDrag<IDragItem, unknown, IDragCollected>(
    {
      type: DraggableTask,
      item: { taskId, stepId },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    },
    [taskId, stepId],
  );

  const [{ isOver }, drop] = useDrop<IDragItem, unknown, IDropCollected>(
    {
      accept: DraggableTask,
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
      hover: (item, monitor) => {
        if (bodyRef.current) {
          const bodyRect = bodyRef.current.getBoundingClientRect();
          const pointerOffset = monitor.getClientOffset();
          if (item.taskId === taskId) setDropPlacement(null);
          else if (bodyRect && pointerOffset) {
            const center = bodyRect.top + bodyRect.height / 2;
            if (pointerOffset.y <= center) setDropPlacement('before');
            else setDropPlacement('after');
          }
        }
      },
      drop: (item) => {
        const flow = getFlow();
        const tasks = uniqBy(flow?.steps ?? [], (s) => s.stepId + s.taskId);
        const targetIndex = tasks.findIndex((s) => s.stepId === stepId && s.taskId === taskId);

        if (tasks[targetIndex]?.stepId === item.stepId) {
          switch (dropPlacement) {
            case 'before':
              const prevTask = tasks[targetIndex - 1];
              if (prevTask?.stepId === item.stepId && prevTask?.taskId === item.taskId) return;
              break;
            case 'after':
              const nextTask = tasks[targetIndex + 1];
              if (nextTask?.stepId === item.stepId && nextTask?.taskId === item.taskId) return;
              break;
          }
        }

        if (flow && targetIndex >= 0 && dropPlacement) {
          const priorTask = tasks[dropPlacement === 'before' ? targetIndex - 1 : targetIndex];

          updateFlow((draft) => {
            const stepsMap = draft.steps.reduce<Record<string, Record<string, IFlowStepConfig[]>>>(
              (acc, item) => {
                acc[item.stepId] ??= {};
                acc[item.stepId][item.taskId] ??= [];
                acc[item.stepId][item.taskId].push(item);
                return acc;
              },
              {},
            );

            if (stepsMap[item.stepId] && stepsMap[item.stepId][item.taskId]) {
              const subjects = stepsMap[item.stepId][item.taskId];
              const sanitizedSubjects = subjects.map((subject) => {
                if (item.stepId !== stepId) {
                  subject = { ...subject, stepId: stepId };
                  editorUtils.applyStepChangesToFlowStepConfig(subject, tasks[targetIndex]);
                }
                return subject;
              });

              if (subjects.length) {
                delete stepsMap[item.stepId][item.taskId];
                draft.steps = Object.entries(stepsMap).reduce<IFlowStepConfig[]>(
                  (acc, [iStepId, taskMap]) => {
                    for (const [iTaskId, items] of Object.entries(taskMap)) {
                      if (iStepId === stepId && iTaskId === taskId) {
                        if (dropPlacement === 'before') {
                          acc.push(...sanitizedSubjects);
                          acc.push(...items);
                        } else {
                          acc.push(...items);
                          acc.push(...sanitizedSubjects);
                        }
                      } else {
                        acc.push(...items);
                      }
                    }
                    return acc;
                  },
                  [],
                );
              }
            }

            return draft;
          });

          registerRequest(
            () =>
              moveTask({
                flowId: flow.id,
                taskId: item.taskId,

                stepId,
                afterTaskId: priorTask?.stepId === stepId ? priorTask.taskId : emptyId,
              }).catch(),
            {
              key: hash(['MOVE_TASK', item.taskId]),
              objectId: item.taskId,
              objectType: 'task',
            },
          );
        }
      },
    },
    [stepId, taskId, getFlow, dropPlacement],
  );

  useEvents((ctx) => {
    Events.ToggleFlowBuilderNavTaskNode.attach(ctx, ({ value }) => handleExpand(value));
  }, []);

  drop(bodyRef);
  preview(bodyRef);

  return (
    <DropBoxBody
      ref={bodyRef}
      data-testid={TaskNodeTestIds.Root}
      data-dragging={isDragging}
      data-task-node-id={taskId}
      data-drop-placement={isOver ? dropPlacement : ''}
      pt={0}
      pb={0}
    >
      <Box borderRadius={1} bgcolor={isDragging ? palette.action.hover : ''}>
        {hasRequest ? (
          <TaskLoading />
        ) : (
          <Box ref={!!canDrag ? drag : undefined} style={{ opacity: isDragging ? 0 : 1 }}>
            <NodeHeader
              canRename
              showContextMenu={!isAssetType}
              label={displayName}
              untitledLabel={t('labels.untitledTask')}
              onAddItem={async () => {
                await addSection(layoutId);
              }}
              isSelected={isAssetType ? false : isSelected}
              addItemTooltip={t('buttons.addSection')}
              actions={[
                {
                  title: t('buttons.delete'),
                  color: palette.error.main,
                  disabled: isSingleStepInFlow && isSingleTask,
                  loading: isDeletingTask || isDeletingStep,
                  execute: async () => {
                    if (isSingleTask) {
                      deleteStep({ stepId }).catch();
                    } else {
                      deleteTask({ taskId }).catch();
                    }
                  },
                },
              ]}
              validationResult={validationResult}
              renderNameTextField={(props) => (
                <TaskNameField
                  taskId={taskId}
                  stepId={stepId}
                  multiline
                  fullWidth
                  variant="standard"
                  disableValidationResultTooltip
                  InputProps={{ sx: { p: 0 } }}
                  inputProps={{
                    sx: {
                      py: 0,
                      px: 0.5,
                      borderRadius: 1.5,
                      backgroundColor: palette.background.card,
                    },
                  }}
                  sx={{ top: 4, minHeight: spacing(3.5) }}
                  {...props}
                />
              )}
              highlightOnHover
              renderStartContent={() => (
                <ExpandButton
                  isExpanded={isExpanded}
                  onToggle={() => handleExpand(!isExpanded)}
                  dataTestId={TaskNodeTestIds.ToggleCollapseButton}
                />
              )}
              onClick={!isAssetType ? onClick : undefined}
            />

            <Collapse in={isExpanded} unmountOnExit>
              <Stack ml={6} pt={sections.length > 0 ? 0.25 : 0} gap={0.25}>
                {sections.map((section) => (
                  <SectionNode
                    key={section.id}
                    canDrag
                    sectionId={section.id}
                    layoutId={layoutId}
                    taskId={taskId}
                    stepId={stepId}
                    level={level + 1}
                    componentIndex={section.index}
                  />
                ))}
                <SectionDropPlaceholder layoutId={layoutId} taskId={taskId} stepId={stepId} />
              </Stack>
            </Collapse>
          </Box>
        )}
      </Box>
    </DropBoxBody>
  );
};

function TaskLoading() {
  return (
    <Box data-testid="builder-explorer-task-skeleton-loader" pt={1}>
      <Skeleton
        count={1}
        itemSx={{ mb: 0 }}
        boxPadding={0}
        renderItem={<DefaultSkeletonLoader layout={taskLoadingProps} />}
      />
    </Box>
  );
}

const taskLoadingProps = [
  {
    variant: 'circular',
    width: 20,
    height: 20,
    sx: {
      ml: 1,
      mb: 3,
      display: 'inline-block',
    },
  },
  {
    variant: 'rounded',
    width: '50%',
    height: 14,
    sx: {
      ml: 1,
      mb: 3.25,
      display: 'inline-block',
    },
  },
  ...Array(2).fill({
    variant: 'rounded',
    width: '75%',
    height: 14,
    sx: {
      ml: 6.5,
      mb: 3,
    },
  }),
];
